import { t } from 'translations'

export type Department = 'marketing' | 'sales' | 'onboarding' | 'tech'

export interface ConviousMetricsValue {
    name: string
    value: number | null
    format: string | null
}

export interface ConviousMetrics {
    name: string
    values: ConviousMetricsValue[]
}

export interface CardList {
    name: string
    dataName: string
    units: string
    department: Department | null
}

export const getWeekDataCardList = (): CardList[] => [
    {
        name: t('admin.conviousMetrics.cards.websiteVisits'),
        dataName: 'Website visitors',
        units: '',
        department: 'marketing',
    },
    {
        name: t('admin.conviousMetrics.cards.downloads'),
        dataName: 'Downloads',
        units: '',
        department: 'marketing',
    },
    {
        name: t('admin.conviousMetrics.cards.demoRequests'),
        dataName: 'Demo requests',
        units: '',
        department: 'marketing',
    },
    {
        name: t('admin.conviousMetrics.cards.socialReach'),
        dataName: 'Social reach',
        units: '',
        department: 'marketing',
    },
    { name: t('admin.conviousMetrics.cards.leads'), dataName: 'Leads', units: '', department: 'marketing' },
    {
        name: t('admin.conviousMetrics.cards.qualifiedLeads'),
        dataName: "MQL's",
        units: '',
        department: 'marketing',
    },
    {
        name: t('admin.conviousMetrics.cards.opportunities'),
        dataName: 'Opportunities',
        units: '',
        department: 'marketing',
    },
    {
        name: t('admin.conviousMetrics.cards.salesQualifiedLeads'),
        dataName: "SQL's",
        units: '',
        department: 'sales',
    },
    {
        name: t('admin.conviousMetrics.cards.companiesTouched'),
        dataName: 'Companies touched',
        units: '',
        department: 'sales',
    },
    {
        name: t('admin.conviousMetrics.cards.demosGiven'),
        dataName: 'Demos given',
        units: '',
        department: 'sales',
    },
    {
        name: t('admin.conviousMetrics.cards.quotesSent'),
        dataName: 'Quotes sent',
        units: '',
        department: 'sales',
    },
    { name: t('admin.conviousMetrics.cards.gmv'), dataName: 'GMV', units: '', department: 'sales' },
    {
        name: t('admin.conviousMetrics.cards.netRevenue'),
        dataName: 'Net revenue',
        units: '',
        department: 'sales',
    },
    {
        name: t('admin.conviousMetrics.cards.inventorySold'),
        dataName: 'Inventory sold',
        units: '',
        department: 'sales',
    },
    {
        name: t('admin.conviousMetrics.cards.conversion'),
        dataName: 'Checkout conversion rate',
        units: '%',
        department: 'sales',
    },
    {
        name: t('admin.conviousMetrics.cards.averageFee'),
        dataName: 'Average fee',
        units: '%',
        department: 'sales',
    },
]

export const getYtdDataCardList = (): CardList[] => [
    {
        name: t('admin.conviousMetrics.cards.newPartners'),
        dataName: 'New partners',
        units: '',
        department: 'sales',
    },
    {
        name: t('admin.conviousMetrics.cards.partners'),
        dataName: 'Current partners',
        units: '',
        department: 'sales',
    },
    {
        name: t('admin.conviousMetrics.cards.ytdRevenue'),
        dataName: 'YTD revenue',
        units: '',
        department: 'sales',
    },
    {
        name: t('admin.conviousMetrics.cards.productsLive'),
        dataName: 'Products live',
        units: '',
        department: 'onboarding',
    },
    {
        name: t('admin.conviousMetrics.cards.signatureToLive'),
        dataName: 'Signature to live',
        units: 'wd',
        department: 'onboarding',
    },
    { name: t('admin.conviousMetrics.cards.velocity'), dataName: 'Velocity', units: '', department: 'tech' },
    { name: t('admin.conviousMetrics.cards.uptime'), dataName: 'Uptime', units: '%', department: 'tech' },
    {
        name: t('admin.conviousMetrics.cards.performance'),
        dataName: 'Performance',
        units: 'ms',
        department: 'tech',
    },
    {
        name: t('admin.conviousMetrics.cards.costPerTransaction'),
        dataName: 'Infra cost/transaction, Eur',
        units: '',
        department: 'tech',
    },
    {
        name: t('admin.conviousMetrics.cards.committedRevenue2021'),
        dataName: 'Committed GMV 2021',
        units: '',
        department: 'sales',
    },
    {
        name: t('admin.conviousMetrics.cards.committedPartners2021'),
        dataName: 'Committed partners 2021',
        units: '',
        department: 'sales',
    },
]

export const getDepartmentMap = () => ({
    marketing: {
        abbr: t('admin.conviousMetrics.departments.marketing.abbr'),
        color: '#61A7F7',
        text: t('admin.conviousMetrics.departments.marketing.name'),
    },
    sales: {
        abbr: t('admin.conviousMetrics.departments.sales.abbr'),
        color: '#9369BD',
        text: t('admin.conviousMetrics.departments.sales.name'),
    },
    onboarding: {
        abbr: t('admin.conviousMetrics.departments.onboarding.abbr'),
        color: '#93E7EB',
        text: t('admin.conviousMetrics.departments.onboarding.name'),
    },
    tech: {
        abbr: t('admin.conviousMetrics.departments.tech.abbr'),
        color: '#C64191',
        text: t('admin.conviousMetrics.departments.tech.name'),
    },
})

export const getLegend = () => [
    {
        status: 'error',
        text: t('admin.conviousMetrics.legend.below40'),
    },
    {
        status: 'warn',
        text: t('admin.conviousMetrics.legend.between40And69'),
    },
    {
        status: 'success',
        text: t('admin.conviousMetrics.legend.atLeast70'),
    },
    {
        status: 'disabled',
        text: t('admin.conviousMetrics.legend.noTarget'),
    },
]

export const colorMap = {
    good: 'success',
    average: 'warn',
    bad: 'error',
}
